import React, { useEffect, useState } from "react";
import { api, mondayOf } from "@/lib/api";
import { BODY_PART_COLOR } from "@/lib/anatomy";
import { Barbell } from "@phosphor-icons/react";

/**
 * Compact "volume by body part" heatmap for the athlete's current week.
 * Renders one row per body-part bucket with a proportional colored bar.
 * Server-side aggregation lives at /api/stats/body-part-volume/:athlete_id.
 */
export default function BodyPartHeatmap({ athleteId, weekStart }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const ws = weekStart || mondayOf();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/stats/body-part-volume/${athleteId}`, { params: { week_start: ws } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ buckets: [], unclassified_movements: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [athleteId, ws]);

  if (loading) {
    return (
      <div className="hs-card p-5" data-testid="body-part-heatmap-loading">
        <div className="hs-label">Volume by body part</div>
        <div className="mt-3 text-sm text-ink-3">Loading…</div>
      </div>
    );
  }

  const buckets = data?.buckets || [];
  const total = buckets.reduce((a, b) => a + (b.volume || 0), 0);
  const totalSets = buckets.reduce((a, b) => a + (b.sets || 0), 0);
  const unclassifiedCount = (data?.unclassified_movements || []).length;

  return (
    <div className="hs-card p-5" data-testid="body-part-heatmap">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="hs-label">This week · volume by body part</div>
          <div className="mt-1 text-ink-2 text-sm">
            {totalSets > 0
              ? `${totalSets} sets across ${buckets.length} body part${buckets.length === 1 ? "" : "s"}`
              : "No sets logged yet this week."}
          </div>
        </div>
        {total > 0 && (
          <div className="font-data text-xs text-ink-3">
            {Math.round(total).toLocaleString()} lb · reps
          </div>
        )}
      </div>

      {buckets.length === 0 ? (
        <div className="mt-4 py-6 text-center border border-dashed border-line rounded-xl">
          <Barbell size={22} className="mx-auto text-ink-3" />
          <div className="mt-2 text-sm text-ink-3">
            Log a set to see how your volume distributes across muscle groups.
          </div>
        </div>
      ) : (
        <ul className="mt-4 space-y-2" data-testid="body-part-heatmap-list">
          {buckets.map((b) => {
            const pct = total > 0 ? Math.max(4, Math.round((b.volume / total) * 100)) : 0;
            const color = BODY_PART_COLOR[b.body_part] || "bg-muted2 text-ink-2";
            return (
              <li key={b.body_part} className="flex items-center gap-3"
                  data-testid={`body-part-heatmap-row-${b.body_part.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className={`shrink-0 w-28 sm:w-32 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-[0.12em] text-center ${color}`}>
                  {b.body_part}
                </div>
                <div className="flex-1 h-6 bg-muted2/60 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${color.split(" ")[0].replace("/10", "/60").replace("-100", "-300").replace("-200", "-300")}`}
                    style={{ width: `${pct}%` }}
                    title={`${Math.round(b.volume).toLocaleString()} lb·reps · ${b.sets} sets`}
                  />
                </div>
                <div className="shrink-0 font-data text-[11px] text-ink-3 w-24 text-right">
                  {b.sets} set{b.sets === 1 ? "" : "s"}
                  {b.volume > 0 ? ` · ${Math.round(b.volume).toLocaleString()}` : ""}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {unclassifiedCount > 0 && (
        <div
          className="mt-3 text-[11px] text-ink-3 italic"
          title={(data.unclassified_movements || []).join(", ")}
        >
          {unclassifiedCount} movement{unclassifiedCount === 1 ? "" : "s"} not classified — tag them in the Library so they show up here.
        </div>
      )}
    </div>
  );
}
